import {
  readControlBoard,
  type BoardCase,
  type BoardTask,
  type BoardTaskStatus,
  type ControlBoard,
} from "@/api/board/boardCaseFields";
import { supabase } from "@/api/supabaseClient";

export type { BoardCase, BoardTask, BoardTaskStatus, ControlBoard };

export type AppNotification = {
  id: string;
  organization_id: string;
  recipient_id: string;
  category: string;
  title: string;
  body: string | null;
  entity_type: string | null;
  entity_id: string | null;
  dedupe_key: string;
  read_at: string | null;
  created_at: string;
};

export type CareTaskRule = {
  id: string;
  organization_id: string;
  kind: "daily_check" | "weight";
  interval_hours: number;
  is_active: boolean;
};

export class BoardApi {
  static async listControlBoard(asOf?: string): Promise<ControlBoard> {
    const { data, error } = await supabase.rpc("list_control_board", {
      p_as_of: asOf ?? new Date().toISOString(),
    });
    if (error) throw error;
    return readControlBoard(data);
  }

  static async listCaseOpenTasks(caseId: string): Promise<BoardTask[]> {
    const { data, error } = await supabase.rpc("list_case_open_tasks", {
      p_case_id: caseId,
      p_as_of: new Date().toISOString(),
    });
    if (error) throw error;
    return (data ?? []) as BoardTask[];
  }

  static async setBoardDensity(density: "compact" | "expanded") {
    const { error } = await supabase.rpc("set_board_density", {
      p_density: density,
    });
    if (error) throw error;
  }

  static async listNotifications(): Promise<AppNotification[]> {
    const { data, error } = await supabase.rpc("list_my_notifications", {
      p_limit: 50,
    });
    if (error) throw error;
    return (data ?? []) as AppNotification[];
  }

  static async markNotificationRead(id: string) {
    const { error } = await supabase.rpc("mark_notification_read", {
      p_id: id,
    });
    if (error) throw error;
  }

  static async sweepCareDueNotifications(): Promise<number> {
    const { data, error } = await supabase.rpc("sweep_care_due_notifications", {
      p_as_of: new Date().toISOString(),
    });
    if (error) throw error;
    return typeof data === "number" ? data : 0;
  }

  static async listCareTaskRules(organizationId: string): Promise<CareTaskRule[]> {
    await supabase.rpc("seed_care_task_rules_for_org", { p_org_id: organizationId });
    const { data, error } = await supabase
      .from("care_task_rules")
      .select("id, organization_id, kind, interval_hours, is_active")
      .eq("organization_id", organizationId)
      .order("kind");
    if (error) throw error;
    return (data ?? []) as CareTaskRule[];
  }

  static async upsertCareTaskRule(input: {
    id?: string;
    organization_id: string;
    kind: "daily_check" | "weight";
    interval_hours: number;
    is_active?: boolean;
  }) {
    const { data, error } = await supabase
      .from("care_task_rules")
      .upsert({
        ...(input.id ? { id: input.id } : {}),
        organization_id: input.organization_id,
        kind: input.kind,
        interval_hours: input.interval_hours,
        is_active: input.is_active ?? true,
      })
      .select("id, organization_id, kind, interval_hours, is_active")
      .single();
    if (error) throw error;
    return data as CareTaskRule;
  }

  static async upsertCaseOverride(input: {
    caseId: string;
    kind: "daily_check" | "weight";
    intervalHours: number;
    isActive?: boolean;
  }) {
    const { data, error } = await supabase.rpc("upsert_case_care_task_override", {
      p_case_id: input.caseId,
      p_kind: input.kind,
      p_interval_hours: input.intervalHours,
      p_is_active: input.isActive ?? true,
    });
    if (error) throw error;
    return data;
  }

  static async listCaseOverrides(caseId: string) {
    const { data, error } = await supabase
      .from("case_care_task_overrides")
      .select("id, case_id, kind, interval_hours, is_active")
      .eq("case_id", caseId);
    if (error) throw error;
    return data ?? [];
  }
}
